/**
 * TaskTeamChart - Gráfico de barras mensal (últimos 12 meses)
 *
 * Componente reutilizável de bar chart Recharts. Originalmente criado para
 * "Tarefas da Equipe", agora generalizado via props opcionais (`title` e
 * `series`) para ser reaproveitado por outros dashboards (ex.: Painel do
 * Afiliado — Leads vs Ganhos).
 *
 * Mantém retrocompatibilidade: se nenhuma `series` for informada, renderiza
 * as duas barras originais ("Criadas" e "Concluídas") usando os mesmos
 * `dataKey` esperados pelos consumidores atuais.
 *
 * @version 1.2.0
 * RELEASE: 2026-04-12 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-30 v1.1.0 - Generalização (title/series opcionais)
 * RELEASE: 2026-04-30 v1.2.0 - Eixo Y + grid horizontal opcionais (showYAxis)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';

/** Configuração de uma série (barra) do gráfico. */
export interface ChartSeries {
  /** Nome do campo no objeto de dados. */
  dataKey: string;
  /** Rótulo exibido na legenda/tooltip. */
  name: string;
  /** Cor da barra (hsl/hex/var). */
  color: string;
}

interface Props {
  /** Linhas do gráfico — cada item deve conter `month_label` no eixo X. */
  data: Array<{ month_label: string; [key: string]: unknown }>;
  /** Título do card. Default mantém o texto original do dashboard de Tarefas. */
  title?: string;
  /** Séries a renderizar. Default: Criadas + Concluídas (compatibilidade). */
  series?: ChartSeries[];
  /**
   * Quando `true`, exibe eixo Y numérico à esquerda e linhas de grade
   * horizontais tracejadas. Default `false` para preservar o visual
   * minimalista original dos dashboards de Tarefas.
   */
  showYAxis?: boolean;
  /**
   * Classe extra para o Card externo. Permite consumidores remover sombra
   * interna (`shadow-none border-0`) quando o gráfico é embutido em outro
   * card que já possui sombra/borda premium.
   */
  cardClassName?: string;
}

// Séries padrão (compatibilidade com TasksDashboardOverview e MyTasksDashboard)
const DEFAULT_SERIES: ChartSeries[] = [
  { dataKey: 'created_count', name: 'Criadas', color: 'hsl(217, 91%, 60%)' },
  { dataKey: 'completed_count', name: 'Concluídas', color: 'hsl(142, 71%, 45%)' },
];

export function TaskTeamChart({
  data,
  title = 'Tarefas da Equipe - Últimos 12 Meses',
  series = DEFAULT_SERIES,
  showYAxis = false,
  cardClassName,
}: Props) {
  return (
    <Card className={cardClassName ?? 'shadow-lg'}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[143px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 10, left: showYAxis ? 0 : -10, bottom: 5 }}>
              {/* Grid horizontal tracejado — apenas quando eixo Y está visível */}
              {showYAxis && (
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                  strokeOpacity={0.6}
                />
              )}
              <XAxis
                dataKey="month_label"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
              />
              {/* Eixo Y: oculto por padrão; visível com ticks e numeração quando showYAxis */}
              {showYAxis ? (
                <YAxis
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                  allowDecimals={false}
                />
              ) : (
                <YAxis hide />
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {series.map((s) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name}
                  fill={s.color}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
